'use client';

import { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { BlockRoot, useCart } from '@akademi/site-builder-runtime';
import { CABINET_BUILDER_CSS } from '../cabinets/cabinetBuilderCss';
import { StandardCabinets } from '../cabinets/StandardCabinets';
import { CarcassChoices, InsidePanel, SizePanel, TypePanel } from '../cabinets/CabinetEditor';
import { cabinetPreview, footprintSvg, measuredDrawing } from '../cabinets/drawings';
import { CARCASS_DRAWING_HEX, checkCabinet, limitsKey, typeName, type Cabinet, type CabinetType, type CornerKind } from '../cabinets/model';
import { cabinetOrderReducer, initialCabinetOrder, type CabinetOrderState, type CabinetPreset } from '../cabinets/orderState';
import { cabinetPriceRequest, keepPendingRequest, takePendingRequest, type PendingRequest } from '../cabinets/priceRequest';
import { NeedsPriceRequest, type CabinetOrderSpec, type CabinetQuote } from '../data/cabinetPricing';
import { formatGbp } from '../data/doorPricing';
import { useSpatialPorts } from '../data/ports';
import { SignInRequired } from '../data/savedDesigns';
import { rangeLook } from '../data/doorCatalogue';
import { useDoorCatalogue } from '../data/useDoorCatalogue';
import { SvgArt } from '../drawing/SvgArt';
import { DOOR_BUILDER_CSS } from '../doors/doorBuilderCss';
import type { HandleProduct } from '../doors/drilling';
import { rangesFor } from '../doors/orderState';
import { ColourPicker, RangePicker } from '../doors/RangePicker';
import { headingLevel, SkLink, text, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * The cabinets page (site mockup `cabinets.html`): ready-made cabinets in the
 * "Catalogue" tab, and "Made to measure" for any size, type and inside
 * (levels 1 and 2 of `SITE_FROM_MOCKUP_PLAN.md` §1).
 *
 * <p>Made to measure is an ORDER of cabinets, each one checked against its
 * type's limits, with the doors and drawer fronts it needs listed and sized
 * from the carcass, a measured drawing, and exact shelf and drawer-front
 * heights (`DOOR_AND_CARCASS_BUILDERS_PLAN.md` B8.1–4). Fronts come from the
 * same door ranges as the doors page, so the two always match.
 *
 * <h3>Addresses</h3>
 * `?type=base|wall|tall|drawers|…` opens that group of the standard range. `?mode=custom` opens
 * made to measure, with `&type=` any cabinet type (`corner`, `corner-blind`,
 * `corner-diag`, `understairs`…) and `&fronts=none` for a carcass only.
 *
 * <h3>Data</h3>
 * Through the Spatial ports only. Standard cabinets are the shop's published
 * range at this shopper's prices, sold through the real basket. Made to
 * measure follows the owner's rule (2026-09-28): a cabinet that is exactly a
 * standard unit has its price and goes in the basket as that unit, with its
 * fronts from the door pricing; a modified one is priced by the shop, because
 * how complex it is cannot be judged automatically. "Request a price" sends
 * the whole order as a price request, the shop prices each cabinet, and the
 * quote appears in My designs to accept into the basket. A guest is sent to
 * sign in and brought back, and the request they asked for is then sent.
 */
export interface SpatialKitchenCabinetBuilderProps {
  title?: Localized;
  intro?: Localized;
  headingLevel?: 'h1' | 'h2';
  /** Which tab opens first. */
  tab?: 'catalogue' | 'custom';
  /** Preset door range and colour for the fronts. */
  rangeId?: string;
  colourId?: string;
  measureHref?: string;
  myDesignsHref?: string;
  /** For questions before asking for a price. */
  contactHref?: string;
  /** Where a guest signs in before a price request is sent; they come back here. */
  signInHref?: string;
}

/** `?priceRequest=resume`: back from signing in with a request to send. */
const RESUME_PARAM = 'priceRequest';

/** The shop supplies no handles: the choice sets how the fronts are drilled, as on the doors page. */
const HANDLES: Array<[HandleProduct, string]> = [
  ['bar', 'Bar (2 holes)'],
  ['knob', 'Knob (1 hole)'],
  ['cup', 'Cup (2 holes)'],
  ['none', 'No holes'],
];

/** `?type=` in made-to-measure mode, including the menu's `corner` and `understairs`. */
export function presetFromType(value: string | null): Pick<CabinetPreset, 'cabinetType' | 'corner'> {
  switch (value) {
    case 'base':
    case 'wall':
    case 'tall':
    case 'drawers':
    case 'wardrobe':
      return { cabinetType: value };
    case 'stairs':
    case 'understairs':
    case 'under-stairs':
      return { cabinetType: 'stairs' };
    case 'corner':
    case 'corner-l':
      return { cabinetType: 'corner', corner: 'l' };
    case 'corner-blind':
    case 'blind':
      return { cabinetType: 'corner', corner: 'blind' };
    case 'corner-diag':
    case 'diagonal':
      return { cabinetType: 'corner', corner: 'diag' };
    default:
      return {};
  }
}

function describe(c: Cabinet): string {
  const size = c.type === 'stairs' ? `${c.w} long, ${c.h}→${c.h2} high` : limitsKey(c.type, c.corner) === 'corner-l' ? `${c.w} × ${c.b} corner, ${c.h} high` : `${c.w} × ${c.h} × ${c.d} mm`;
  return `${size}${c.fronts ? '' : ' · carcass only'} · ×${c.qty}`;
}

export function SpatialKitchenCabinetBuilder(props: SpatialKitchenCabinetBuilderProps) {
  const id = useId();
  const artId = useId();
  const { cabinetCatalogue, cabinetPricing, savedDesigns, cabinetBasket, priceRequests } = useSpatialPorts();
  const doors = useDoorCatalogue();
  const catalogue = doors.status === 'ready' ? doors.catalogue : undefined;
  const reducer = useMemo(() => cabinetOrderReducer(catalogue), [catalogue]);
  const [state, dispatch] = useReducer(reducer, undefined, () => initialCabinetOrder(catalogue, { rangeId: props.rangeId, colourId: props.colourId }));
  const [tab, setTab] = useState<'catalogue' | 'custom'>(props.tab ?? 'catalogue');
  const [initialGroup, setInitialGroup] = useState<string | undefined>(undefined);
  const [view, setView] = useState<'look' | 'measured'>('look');
  const [quote, setQuote] = useState<CabinetQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  /** Set when the order holds a modified cabinet: cabinet i's standard unit, or undefined. */
  const [onRequest, setOnRequest] = useState<ReadonlyArray<string | undefined> | null>(null);
  /** Where a standard cabinet still cannot be priced online, the shop's reason. */
  const [requestReasons, setRequestReasons] = useState<ReadonlyArray<string | undefined>>([]);
  const cart = useCart();
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [requested, setRequested] = useState<{ kind: 'sent' | 'error' | 'signin'; message: string } | null>(null);
  const [resume, setResume] = useState<PendingRequest | null>(null);
  const preferred = useRef<{ rangeId?: string; colourId?: string }>({ rangeId: props.rangeId, colourId: props.colourId });

  // The address can preset the page; read after mount so server and first client render match.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(RESUME_PARAM) === 'resume') {
      // Back from signing in: put the order back and send the request the shopper asked for.
      params.delete(RESUME_PARAM);
      const rest = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
      const pending = takePendingRequest();
      setTab('custom');
      if (pending) {
        dispatch({ type: 'restore', order: pending.order });
        setMessage(pending.message);
        setPhone(pending.phone);
        setResume(pending);
      }
      return;
    }
    const type = params.get('type');
    if (params.get('mode') === 'custom') {
      setTab('custom');
      const preset = presetFromType(type);
      if (preset.cabinetType === 'corner') dispatch({ type: 'corner', corner: preset.corner as CornerKind });
      else if (preset.cabinetType) dispatch({ type: 'cabinetType', cabinetType: preset.cabinetType as CabinetType });
      if (params.get('fronts') === 'none') dispatch({ type: 'fronts', on: false });
    } else if (type) {
      setInitialGroup(type);
    }
    const range = params.get('range');
    if (range) dispatch({ type: 'range', rangeId: range });
    const colour = params.get('colour');
    if (colour) dispatch({ type: 'colour', colourId: colour });
    preferred.current = { rangeId: range ?? preferred.current.rangeId, colourId: colour ?? preferred.current.colourId };
  }, []);

  // The door ranges load after mount: once they are in, an order with no range yet takes the
  // one the address or the block asked for, or the first.
  useEffect(() => {
    if (!catalogue || state.rangeId) return;
    const kitchen = rangesFor(catalogue, 'kitchen');
    const wanted = kitchen.find((r) => r.id === preferred.current.rangeId) ?? kitchen[0];
    if (!wanted) return;
    dispatch({ type: 'range', rangeId: wanted.id });
    if (preferred.current.colourId) dispatch({ type: 'colour', colourId: preferred.current.colourId });
  }, [catalogue, state.rangeId]);

  const spec: CabinetOrderSpec = useMemo(
    () => ({
      rangeId: state.rangeId,
      colourId: state.colourId,
      ...(state.colourId === 'ral' ? { paintColour: state.paintColour.trim() } : {}),
      handle: state.handle,
      cabinets: state.cabinets,
    }),
    [state.rangeId, state.colourId, state.paintColour, state.handle, state.cabinets],
  );
  const checks = state.cabinets.map(checkCabinet);
  const valid = checks.every((c) => c.ok);

  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    setOnRequest(null);
    setRequestReasons([]);
    if (!valid || !cabinetPricing) return;
    let current = true;
    cabinetPricing
      .quote(spec)
      .then((q) => {
        if (current) setQuote(q);
      })
      .catch((error: unknown) => {
        if (!current) return;
        if (error instanceof NeedsPriceRequest) {
          setOnRequest(error.standard);
          setRequestReasons(error.reasons);
        }
        else setQuoteError(error instanceof Error ? error.message : 'The price could not be worked out.');
      });
    return () => {
      current = false;
    };
  }, [cabinetPricing, spec, valid]);

  const ranges = catalogue ? rangesFor(catalogue, 'kitchen') : [];
  const range = catalogue?.ranges().find((r) => r.id === state.rangeId) ?? ranges[0];
  const colour = catalogue?.colours().find((c) => c.id === state.colourId);
  const colourHex = colour?.hex ?? '#e9e4da';
  const active = state.cabinets[state.active] ?? (state.cabinets[0] as Cabinet);
  const activeCheck = checks[state.active] ?? { ok: true, issues: [] };
  const look = { style: range?.style ?? 'slab', doorLook: range ? rangeLook(range) : undefined, colour: colourHex, wood: Boolean(colour?.wood), carcassHex: CARCASS_DRAWING_HEX, idPrefix: artId };
  const Heading = headingLevel(props.headingLevel, 'h1');
  const myDesignsHref = props.myDesignsHref || '/my-designs';
  const canOrder = Boolean(cabinetBasket) || savedDesigns.saves.has('cabinets');

  async function order() {
    if (!quote || !valid) return;
    setBusy(true);
    setStatus(null);
    try {
      if (cabinetBasket) {
        await cabinetBasket.add(spec);
        await cart?.refresh();
        setStatus({ kind: 'ok', message: 'Added to your basket.' });
      } else {
        const first = state.cabinets[0] as Cabinet;
        await savedDesigns.save({
          kind: 'cabinets',
          title: state.cabinets.length === 1 ? typeName(first) : `${quote.cabinets} made-to-measure cabinets`,
          summary: `${range ? `${range.name} · ${colour?.name ?? ''} · ` : ''} ${quote.cabinets} cabinet${quote.cabinets === 1 ? '' : 's'}`,
          href: `/cabinets?mode=custom&range=${encodeURIComponent(state.rangeId)}&colour=${encodeURIComponent(state.colourId)}`,
          spec,
          total: quote.total,
          thumbnailSvg: cabinetPreview(first, { ...look, idPrefix: 'thumb' }),
        });
        setStatus({ kind: 'ok', message: 'Saved to My designs.' });
      }
    } catch (error) {
      setStatus({ kind: 'error', message: error instanceof Error ? error.message : 'That did not go through. Please try again.' });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Sends the order as a price request. A settled "not signed in" (401) keeps the order in this
   * tab and sends the shopper to sign in, coming back here to send it; any other failure is said
   * on the page and nobody is redirected. Coming back, a second 401 is not followed round again.
   */
  async function requestPrice(order: CabinetOrderState, extra: { message: string; phone: string }, returning: boolean) {
    if (!priceRequests) return;
    if (!order.cabinets.map(checkCabinet).every((c) => c.ok)) {
      setRequested({ kind: 'error', message: 'Check the cabinets marked in your list first.' });
      return;
    }
    setBusy(true);
    setRequested(null);
    try {
      const spec: CabinetOrderSpec = {
        rangeId: order.rangeId,
        colourId: order.colourId,
        ...(order.colourId === 'ral' ? { paintColour: (order.paintColour ?? '').trim() } : {}),
        handle: order.handle,
        cabinets: order.cabinets,
      };
      await priceRequests.request(cabinetPriceRequest(spec, catalogue, extra));
      setMessage('');
      setRequested({ kind: 'sent', message: 'Sent. We will price every cabinet and your quote will appear in My designs.' });
    } catch (error) {
      if (error instanceof SignInRequired && !returning) {
        keepPendingRequest({ order, message: extra.message, phone: extra.phone });
        const back = new URL(window.location.href);
        back.searchParams.set('mode', 'custom');
        back.searchParams.set(RESUME_PARAM, 'resume');
        const base = props.signInHref?.trim() || '/sign-in';
        window.location.assign(`${base}${base.includes('?') ? '&' : '?'}next=${encodeURIComponent(`${back.pathname}${back.search}`)}`);
        return;
      }
      setRequested(
        error instanceof SignInRequired
          ? { kind: 'signin', message: 'Please sign in to send your request.' }
          : { kind: 'error', message: error instanceof Error && error.message ? error.message : 'Your request did not go through. Please try again.' },
      );
    } finally {
      setBusy(false);
    }
  }

  // Back from signing in: send the kept request once the door ranges have settled, so the
  // description names the range and colour the shopper chose.
  useEffect(() => {
    if (!resume || doors.status === 'loading') return;
    setResume(null);
    void requestPrice(resume.order, { message: resume.message, phone: resume.phone }, true);
  }, [resume, doors.status]);

  return (
    <BlockRoot as="section" className="sk sk-db sk-cb" aria-labelledby={`${id}-title`}>
      <SkBaseStyles />
      <SkStyles id="door-builder" css={DOOR_BUILDER_CSS} />
      <SkStyles id="cabinet-builder" css={CABINET_BUILDER_CSS} />
      <div className="sk-db__wrap">
        <div className="sk-db__head">
          <div>
            <Heading id={`${id}-title`} className="sk-heading sk-db__title">
              {text(props.title, 'Kitchen cabinets')}
            </Heading>
            <p className="sk-lead">
              {text(props.intro, 'Choose a standard cabinet, or have one made to the millimetre: any width, height and depth, corners and under-stairs runs included.')}
            </p>
          </div>
        </div>
        <div className="sk-cb__tabs" role="tablist" aria-label="Cabinets">
          <button type="button" role="tab" id={`${id}-tab-cat`} aria-controls={`${id}-cat`} aria-selected={tab === 'catalogue'} onClick={() => setTab('catalogue')}>
            Standard sizes
          </button>
          <button type="button" role="tab" id={`${id}-tab-custom`} aria-controls={`${id}-custom`} aria-selected={tab === 'custom'} onClick={() => setTab('custom')}>
            Made to measure
          </button>
        </div>
        <div role="tabpanel" id={`${id}-cat`} aria-labelledby={`${id}-tab-cat`} hidden={tab !== 'catalogue'}>
          <StandardCabinets catalogue={cabinetCatalogue} initialGroup={initialGroup} colour={colourHex} onCustom={() => setTab('custom')} />
        </div>

        <div role="tabpanel" id={`${id}-custom`} aria-labelledby={`${id}-tab-custom`} hidden={tab !== 'custom'}>
          <div className="sk-db__grid">
            <div className="sk-db__stage">
              <span className="sk-db__tag">{`${typeName(active)}${active.fronts ? '' : ' · carcass only'}`}</span>
              <div className="sk-seg sk-db__viewseg" role="group" aria-label="View">
                <button type="button" aria-pressed={view === 'look'} onClick={() => setView('look')}>
                  Preview
                </button>
                <button type="button" aria-pressed={view === 'measured'} onClick={() => setView('measured')}>
                  Measured
                </button>
              </div>
              <div className="sk-db__canvas">
                {view === 'look' ? (
                  <SvgArt className="sk-cb__preview" svg={cabinetPreview(active, look)} />
                ) : (
                  <SvgArt className="sk-cb__measure" svg={measuredDrawing(active)} />
                )}
              </div>
              <div className="sk-cb__plan" aria-hidden="true">
                <SvgArt svg={footprintSvg(active, `${artId}fp`)} />
                FROM ABOVE
              </div>
              <div className="sk-db__dims">
                <span>{describe(active)}</span>
                <span>{`${active.build === 'rigid' ? 'Rigid' : 'Flat-pack'} · 18mm board`}</span>
              </div>
            </div>

            <div>
              <TypePanel cabinet={active} index={state.active} dispatch={dispatch} />
              <SizePanel cabinet={active} index={state.active} dispatch={dispatch} measureHref={props.measureHref} />
              <InsidePanel cabinet={active} index={state.active} dispatch={dispatch} />
              {!activeCheck.ok && (
                <div className="sk-cb__issues" role="status">
                  <strong>Check this cabinet</strong>
                  <ul>
                    {activeCheck.issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="sk-db__panel">
                <div className="sk-db__step">
                  <span className="sk-db__n" aria-hidden="true">
                    4
                  </span>
                  <h2 className="sk-heading">{catalogue ? 'Door range and finish' : 'Doors and delivery'}</h2>
                  <span className="sk-db__aside">For every cabinet in this order</span>
                </div>
                {!catalogue || !range ? (
                  <p className="sk-cb__note" style={{ marginTop: 0 }}>
                    {doors.status === 'loading'
                      ? 'Loading the door ranges…'
                      : 'Tell us the door range and colour you want in the note below. The fronts each cabinet needs are listed above, sized to the carcass.'}
                  </p>
                ) : state.cabinets.some((c) => c.fronts) ? (
                  <>
                    <RangePicker catalogue={catalogue} ranges={ranges} range={range} colourId={state.colourId} onPick={(rangeId) => dispatch({ type: 'range', rangeId })} />
                    <div style={{ marginTop: '1rem' }}>
                      <ColourPicker
                        catalogue={catalogue}
                        range={range}
                        colourId={state.colourId}
                        onPick={(colourId) => dispatch({ type: 'colour', colourId })}
                        paintColour={state.paintColour}
                        onPaintColour={(value) => dispatch({ type: 'paintColour', value })}
                      />
                    </div>
                    <div className="sk-cb__sub">
                      <span className="sk-db__label" id={`${id}-handles`}>
                        Drilling for your handles
                      </span>
                      <div className="sk-seg" role="group" aria-labelledby={`${id}-handles`}>
                        {HANDLES.map(([handle, label]) => (
                          <button key={handle} type="button" aria-pressed={state.handle === handle} disabled={range.noHandle} onClick={() => dispatch({ type: 'handleProduct', handle })}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="sk-cb__note" style={{ marginTop: 0 }}>
                    Carcasses only: no doors or drawer fronts in this order.
                  </p>
                )}
                <CarcassChoices cabinet={active} index={state.active} dispatch={dispatch} />
              </div>

              <div className="sk-db__panel">
                <div className="sk-db__step">
                  <span className="sk-db__n" aria-hidden="true">
                    5
                  </span>
                  <h2 className="sk-heading">Your cabinets</h2>
                  <span className="sk-db__aside">{`${state.cabinets.length} in this order`}</span>
                </div>
                <ol className="sk-cb__order">
                  {state.cabinets.map((c, i) => (
                    <li key={i} className="sk-cb__orow" data-active={i === state.active}>
                      <button type="button" aria-pressed={i === state.active} onClick={() => dispatch({ type: 'select', index: i })}>
                        <strong>{`${i + 1}. ${typeName(c)}`}</strong>
                        <span>
                          {describe(c)}
                          {quote?.lines[i] ? ` · ${formatGbp(quote.lines[i]!.total)}` : ''}
                          {onRequest
                            ? onRequest[i]
                              ? ' · standard size'
                              : requestReasons[i]
                                ? ` · priced on request: ${requestReasons[i]}`
                                : ' · made to measure, priced on request'
                            : ''}
                          {checks[i]?.ok ? '' : ' · needs checking'}
                        </span>
                      </button>
                      <span className="sk-cb__oact">
                        <button type="button" aria-label={`Copy cabinet ${i + 1}`} onClick={() => dispatch({ type: 'duplicate', index: i })}>
                          Copy
                        </button>
                        <button type="button" aria-label={`Remove cabinet ${i + 1}`} disabled={state.cabinets.length <= 1} onClick={() => dispatch({ type: 'remove', index: i })}>
                          Remove
                        </button>
                      </span>
                    </li>
                  ))}
                </ol>
                <button type="button" className="sk-db__add" style={{ marginTop: 8 }} onClick={() => dispatch({ type: 'add' })}>
                  + Add another cabinet
                </button>
              </div>

              <div className="sk-db__summary" aria-live="polite">
                {quote ? (
                  <>
                    {(() => {
                      const sum = (key: 'carcass' | 'fronts' | 'hardware') =>
                        quote.lines.reduce((s, l, i) => s + l[key] * (state.cabinets[i]?.qty ?? 1), 0);
                      const units = quote.lines.map((l) => l.standard).filter(Boolean);
                      return (
                        <>
                          <div className="sk-db__line">
                            <span>{`${quote.cabinets} standard carcass${quote.cabinets === 1 ? '' : 'es'}${units.length === 1 ? `: ${units[0]}` : ''}`}</span>
                            <span>{formatGbp(sum('carcass'))}</span>
                          </div>
                          {sum('fronts') > 0 && (
                            <div className="sk-db__line">
                              <span>Doors and drawer fronts</span>
                              <span>{formatGbp(sum('fronts'))}</span>
                            </div>
                          )}
                          {sum('hardware') > 0 && (
                            <div className="sk-db__line">
                              <span>Hinge and handle drilling</span>
                              <span>{formatGbp(sum('hardware'))}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="sk-db__line">
                      <span>Delivery</span>
                      <span>Priced at checkout</span>
                    </div>
                    <div className="sk-db__total">
                      <span>Total inc. VAT</span>
                      <strong>{formatGbp(quote.total)}</strong>
                    </div>
                    <button type="button" className="sk-btn sk-btn--lg" disabled={busy || !canOrder} onClick={order}>
                      {cabinetBasket ? 'Add to basket' : 'Save to My designs'}
                    </button>
                  </>
                ) : cabinetPricing && !onRequest && !quoteError ? (
                  <p className="sk-db__line" style={{ margin: 0 }}>
                    {valid ? 'Working out your price…' : 'Check the cabinets marked in your list to see a price.'}
                  </p>
                ) : (
                  <>
                    <p className="sk-db__line" style={{ margin: 0 }}>
                      {quoteError
                        ? `${quoteError} We can price this order for you instead.`
                        : !cabinetPricing
                          ? 'Every made-to-measure cabinet is priced by us. Send us this order and we will quote each cabinet; the quote appears in My designs.'
                          : requestReasons.some(Boolean)
                            ? 'The shop cannot price every part of this order online (see your cabinets above). Send it to us and we will quote it; the quote appears in My designs.'
                            : state.colourId === 'ral' && onRequest?.every(Boolean)
                            ? 'Name the colour to paint, above, and the price appears here.'
                            : 'Standard sizes are priced online. A cabinet you have changed is made to measure, so we price it: send us this order and the quote appears in My designs.'}
                    </p>
                    {priceRequests ? (
                      <form
                        className="sk-cb__request"
                        onSubmit={(event) => {
                          event.preventDefault();
                          void requestPrice(state, { message, phone }, false);
                        }}
                      >
                        <label htmlFor={`${id}-req-msg`}>Anything we should know? (optional)</label>
                        <textarea id={`${id}-req-msg`} rows={3} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} />
                        <label htmlFor={`${id}-req-phone`}>Phone, if you would like a call (optional)</label>
                        <input id={`${id}-req-phone`} type="tel" autoComplete="tel" maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} />
                        <button type="submit" className="sk-btn sk-btn--lg" disabled={busy || !valid}>
                          {busy ? 'Sending…' : 'Request a price'}
                        </button>
                        {requested && (
                          <p className="sk-db__status" role={requested.kind === 'sent' ? 'status' : 'alert'}>
                            {requested.message}{' '}
                            {requested.kind === 'sent' && <SkLink href={myDesignsHref}>Open My designs</SkLink>}
                            {requested.kind === 'signin' && <SkLink href={props.signInHref || '/sign-in'}>Sign in</SkLink>}
                          </p>
                        )}
                        <small>
                          Questions first? <SkLink href={props.contactHref || '/contact'}>Contact us</SkLink>
                        </small>
                      </form>
                    ) : (
                      <SkLink href={props.contactHref || '/contact'} className="sk-btn sk-btn--lg">
                        Ask us for a price
                      </SkLink>
                    )}
                  </>
                )}
                {status && (
                  <p className="sk-db__status" role={status.kind === 'error' ? 'alert' : 'status'}>
                    {status.message} {status.kind === 'ok' && !cabinetBasket && <SkLink href={myDesignsHref}>Open My designs</SkLink>}
                  </p>
                )}
                <small>
                  Every cabinet is made to order.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlockRoot>
  );
}
